import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Space, Tabs, message } from "antd";
import { getCampaignFormValues } from "./campaignForm";
import { localDate } from "./formatters";
import { request, root } from "./rewardsApi";
import exportAwards from "./exportAwards";
import RewardsGuide from "./components/RewardsGuide";
import RewardsToolbar from "./components/RewardsToolbar";
import CampaignModal from "./components/CampaignModal";
import RefundModal from "./components/RefundModal";
import ReverseAwardModal from "./components/ReverseAwardModal";
import CampaignsTable from "./components/CampaignsTable";
import AwardsTable from "./components/AwardsTable";
import ReferralsTable from "./components/ReferralsTable";
import PaymentsTable from "./components/PaymentsTable";
import LedgerTable from "./components/LedgerTable";
import AuditTable from "./components/AuditTable";

function Rewards() {
  const [showGuide, setShowGuide] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(0);
  const [campaign, setCampaign] = useState(null);
  const [user, setUser] = useState(null);
  const [editing, setEditing] = useState(null);
  const [reversing, setReversing] = useState(null);
  const [refunding, setRefunding] = useState(null);
  const [campuses, setCampuses] = useState([]);
  const query = useCallback(
    (p) =>
      `?page=${p}${campaign ? `&campaign_id=${campaign}` : ""}${
        user ? `&user_id=${user}` : ""
      }`,
    [campaign, user]
  );
  const load = useCallback(async () => {
    setBusy(true);
    setError("");
    try {
      setData(await request(root + query(page)));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }, [page, query]);
  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    request("/campuses")
      .then((x) => setCampuses(x.results || []))
      .catch(() => {});
  }, []);
  const edit = (row) => {
    const value = row || {
      name: "Campus referral and ride rewards",
      status: "draft",
      budget_paise: 1000000,
      rules: data.defaults,
    };
    try {
      const values = getCampaignFormValues(value, data.defaults);
      setEditing({
        ...values,
        starts_at: localDate(value.starts_at),
        ends_at: localDate(value.ends_at),
      });
    } catch (error) {
      message.error(error.message);
    }
  };
  const save = async (values) => {
    setSaving(true);
    try {
      await request(
        root + "/campaigns" + (editing.id ? `/${editing.id}` : ""),
        editing.id ? "PUT" : "POST",
        {
          ...values,
          budget_paise: Math.round(values.budget * 100),
          starts_at: values.starts_at
            ? new Date(values.starts_at).toISOString()
            : undefined,
          ends_at: values.ends_at
            ? new Date(values.ends_at).toISOString()
            : undefined,
        }
      );
      setEditing(null);
      message.success("Campaign saved");
      await load();
    } catch (e) {
      message.error(e.message);
    } finally {
      setSaving(false);
    }
  };
  const reverse = async (values) => {
    setSaving(true);
    try {
      await request(`${root}/awards/${reversing.id}/reverse`, "POST", values);
      setReversing(null);
      message.success("Award reversed");
      await load();
    } catch (e) {
      message.error(e.message);
    } finally {
      setSaving(false);
    }
  };
  const refund = async (values) => {
    setSaving(true);
    try {
      await request(`${root}/checkouts/${refunding.id}/refund`, "POST", values);
      setRefunding(null);
      message.success("Refund reconciled");
      await load();
    } catch (e) {
      message.error(e.message);
    } finally {
      setSaving(false);
    }
  };
  const reconcile = async (row) => {
    setSaving(true);
    try {
      await request(`${root}/checkouts/${row.id}/reconcile`, "POST", {});
      await load();
      message.success("Payment checked with gateway");
    } catch (e) {
      message.error(e.message);
    } finally {
      setSaving(false);
    }
  };
  const exportRows = async () => {
    setSaving(true);
    try {
      let rows = [];
      let p = 0;
      let result;
      do {
        result = await request(root + query(p++));
        rows.push(...result.awards);
      } while (rows.length < result.award_count && result.awards.length);
      exportAwards(rows);
    } catch (e) {
      message.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="large">
      <RewardsGuide visible={showGuide} onClose={() => setShowGuide(false)} />
      {error && (
        <Alert
          type="error"
          showIcon
          message={error}
          action={<Button onClick={load}>Retry</Button>}
        />
      )}
      <RewardsToolbar
        data={data}
        busy={busy}
        saving={saving}
        campaign={campaign}
        user={user}
        setCampaign={setCampaign}
        setUser={setUser}
        setPage={setPage}
        edit={edit}
        load={load}
        exportRows={exportRows}
        setShowGuide={setShowGuide}
      />
      {data && (
        <Tabs defaultActiveKey="campaigns">
          <Tabs.TabPane tab="Campaigns" key="campaigns">
            <CampaignsTable data={data} edit={edit} />
          </Tabs.TabPane>
          <Tabs.TabPane tab="Awards" key="awards">
            <AwardsTable
              data={data}
              busy={busy}
              page={page}
              setPage={setPage}
              onReverse={setReversing}
            />
          </Tabs.TabPane>
          <Tabs.TabPane tab="Referrals" key="referrals">
            <ReferralsTable data={data} page={page} setPage={setPage} />
          </Tabs.TabPane>
          <Tabs.TabPane tab="Payments and refunds" key="checkouts">
            <PaymentsTable
              data={data}
              page={page}
              setPage={setPage}
              saving={saving}
              reconcile={reconcile}
              onRefund={setRefunding}
            />
          </Tabs.TabPane>
          <Tabs.TabPane tab="Ledger totals" key="totals">
            <LedgerTable data={data} />
          </Tabs.TabPane>
          <Tabs.TabPane tab="Audit trail" key="audit">
            <AuditTable data={data} />
          </Tabs.TabPane>
        </Tabs>
      )}
      {editing && (
        <CampaignModal
          editing={editing}
          saving={saving}
          campuses={campuses}
          defaults={data?.defaults}
          onSave={save}
          onCancel={() => setEditing(null)}
        />
      )}
      {refunding && (
        <RefundModal
          refunding={refunding}
          saving={saving}
          onSave={refund}
          onCancel={() => setRefunding(null)}
        />
      )}
      {reversing && (
        <ReverseAwardModal
          reversing={reversing}
          saving={saving}
          onSave={reverse}
          onCancel={() => setReversing(null)}
        />
      )}
    </Space>
  );
}
export default Rewards;
